import { formatDateToDays, getIsoStringWithoutTime } from '@bos/utils';
import { validateBankAccountNumber, validateBankSortCode } from '@pbk/utils';
import memoize from 'lodash/memoize';
import { useMemo } from 'react';
import { z } from 'zod';
import { MOBILITY_DEFAULT_CURRENCY_CODE, MobilityScope, POSITIVE_VALUE, WORK_DAY_FLAG, } from '../const';
import { getMinMaxDate } from '../helpers/getMinMaxDate';
const MINIMAL_SCOPE_LENGTH = 1;
export const useMobilityFormSchema = ({ todayInfo, mobilityDateNotLoadedError, errorTranslation, getMobilityDateError, getInfoAboutDate, }) => {
    const memoizedGetInfoAboutDateMutation = useMemo(() => {
        return memoize((isoDate) => new Promise((resolve) => {
            getInfoAboutDate({ date: isoDate }, {
                onSuccess: (data) => resolve(data.data),
                onError: () => {
                    resolve(undefined);
                },
            });
        }));
    }, []);
    const schema = z.object({
        otherBankPrefix: z
            .string()
            .optional()
            .refine(validateBankSortCode)
            .prefault(''),
        otherBankAccount: z.string().refine(validateBankAccountNumber),
        otherBankCode: z.string(),
        date: z.date().superRefine(async (date, ctx) => {
            const infoAboutDate = await memoizedGetInfoAboutDateMutation(getIsoStringWithoutTime(date));
            if (!todayInfo || !infoAboutDate) {
                ctx.addIssue({
                    code: 'custom',
                    message: mobilityDateNotLoadedError,
                });
                return;
            }
            const isWorkDay = infoAboutDate?.additionalFields?.find((field) => field.fieldName === WORK_DAY_FLAG)?.fieldValue === POSITIVE_VALUE;
            const { minDate, maxDate } = getMinMaxDate(todayInfo);
            if (!minDate || !maxDate)
                return;
            const chosenDate = infoAboutDate?.code;
            const isChosenDateInRange = minDate <= chosenDate && chosenDate <= maxDate;
            if (!isWorkDay || !isChosenDateInRange) {
                ctx.addIssue({
                    code: 'custom',
                    message: getMobilityDateError({
                        minDate: formatDateToDays(minDate),
                        maxDate: formatDateToDays(maxDate),
                    }),
                });
            }
        }),
        scope: z
            .array(z.enum(MobilityScope))
            .min(MINIMAL_SCOPE_LENGTH, errorTranslation.mobilityScopeMinLength)
            .default([
            MobilityScope.TRANSFER_BALANCE,
            MobilityScope.TRANSFER_ALL_STANDING_ORDERS_AND_DIRECT_DEBITS,
        ]),
        otherBankCurrencyCode: z.enum([MOBILITY_DEFAULT_CURRENCY_CODE]),
    });
    return {
        schema,
    };
};
